import { useEffect, useState, useMemo } from "react";
import { InfoHint } from "../components/InfoHint";
import { api, formatApiError } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { DatePickerField } from "../components/DatePickerField";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PhotoUploader } from "../components/PhotoUploader";
import { DocumentUploader } from "../components/DocumentUploader";
import { PrivacyConsent } from "../components/PrivacyConsent";
import {
  Zap, Droplets, Hammer, KeyRound, ArrowUpDown, Flame, Home, Sparkles, Trees, HelpCircle,
  Umbrella, FireExtinguisher, Briefcase, Building2, Waves, Scale, Antenna,
  ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Tags, ChevronDown, X, Users,
  UserCheck, CheckSquare, Square,
} from "lucide-react";
import { CategorieManager } from "../components/CategorieManager";
import { Cockroach } from "../components/CategoryIcon";

const ICONS = {
  Elettrica: Zap, Idraulica: Droplets, Muraria: Hammer, "Serramenti/Fabbro": KeyRound,
  Ascensore: ArrowUpDown, Antennista: Antenna, "Riscaldamento/Caldaia": Flame, "Tetto/Lattoneria": Home,
  Pulizie: Sparkles, Giardinaggio: Trees,
  Assicurazione: Umbrella, Antincendio: FireExtinguisher, Professionista: Briefcase,
  "Studi Amministrazione": Building2, "Avvocato-Studi Legali": Scale, Disinfestazioni: Cockroach, Spurghi: Waves,
  Altro: HelpCircle,
};

const CAT_COLORS = {
  Elettrica: "text-yellow-500", Idraulica: "text-blue-600", Muraria: "text-amber-700",
  "Serramenti/Fabbro": "text-slate-600", Ascensore: "text-teal-600", Antennista: "text-fuchsia-600", "Riscaldamento/Caldaia": "text-red-600",
  "Tetto/Lattoneria": "text-orange-700", Pulizie: "text-cyan-600", Giardinaggio: "text-green-600",
  Assicurazione: "text-indigo-600", Antincendio: "text-red-700", Professionista: "text-violet-600",
  "Studi Amministrazione": "text-violet-600", "Avvocato-Studi Legali": "text-indigo-700", Disinfestazioni: "text-emerald-600", Spurghi: "text-sky-600",
  Altro: "text-slate-500",
};

const STEPS = ["Categoria", "Dettagli", "Riepilogo"];

// Giorni SLA di fallback (allineati al backend) per suggerire la scadenza
const SLA_GIORNI = { Critica: 1, Alta: 3, Media: 7, Bassa: 15 };
const PRIORITA_STYLE = {
  Bassa: "border-[#CBD5E1] bg-[#F1F5F9] text-[#475569]",
  Media: "border-[#FDE68A] bg-[#FEF3C7] text-[#B45309]",
  Alta: "border-[#FDBA74] bg-[#FFEDD5] text-[#C2410C]",
  Critica: "border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]",
};

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function NewTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  // Iter122i · scroll a inizio pagina ad ogni cambio di step del wizard (utile su mobile).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);
  const [meta, setMeta] = useState({ categorie: [], urgenze: [], tipi: [], priorita: [] });
  const [condomini, setCondomini] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [allegati, setAllegati] = useState([]);
  const [scadenzaAuto, setScadenzaAuto] = useState(true);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [recenti, setRecenti] = useState([]);
  const [dupAck, setDupAck] = useState(false);
  const [showDup, setShowDup] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [contatti, setContatti] = useState([]);
  const [contattiOpen, setContattiOpen] = useState(false);
  const [filtroRuolo, setFiltroRuolo] = useState(null); // null = tutti; ["Proprietario","Comproprietario"]; ["Inquilino/Conduttore"]
  const contattiFiltrati = useMemo(() => {
    if (!filtroRuolo) return contatti;
    const set = new Set(filtroRuolo);
    return contatti.filter((c) => {
      const ruoli = (c.ruolo || "").split(",").map((r) => r.trim()).filter(Boolean);
      return ruoli.some((r) => set.has(r));
    });
  }, [contatti, filtroRuolo]);
  // Gestione (solo Studio/admin): affido immediato a uno o più fornitori
  const [suggested, setSuggested] = useState([]);
  const [fornSearch, setFornSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selForn, setSelForn] = useState([]);
  const [assignNote, setAssignNote] = useState("");
  const loadMeta = () => api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  const [form, setForm] = useState({
    condominio_id: user.role === "condomino" ? user.condominio_id : "",
    categoria: "", urgenza: "Non urgente", priorita: "Media", tipo: "Ordinaria",
    data_scadenza: addDays(SLA_GIORNI.Media),
    titolo: "", descrizione: "",
    condomino_nome: user.role === "condomino" ? user.name : "",
    // Iter 2 refactor: unità pre-compilata dal contesto anagrafico (nuovo o legacy)
    unita: user.role === "condomino" ? (user.unita || "") : "",
    telefono_contatto: user.role === "condomino" ? (user.phone || "") : "",
    contatti_referenti: [],
  });
  // Memoria dell'ultima scelta filtro Ruoli per condominio (localStorage) — DEVE stare dopo `form`.
  useEffect(() => {
    if (!form.condominio_id) return;
    try {
      const stored = JSON.parse(localStorage.getItem("newticket_filtro_ruolo") || "{}");
      const val = stored[form.condominio_id];
      setFiltroRuolo(val === undefined ? null : val);
    } catch { setFiltroRuolo(null); }
  }, [form.condominio_id]);
  const setAndRememberFiltro = (roles) => {
    setFiltroRuolo(roles);
    if (!form.condominio_id) return;
    try {
      const stored = JSON.parse(localStorage.getItem("newticket_filtro_ruolo") || "{}");
      stored[form.condominio_id] = roles;
      localStorage.setItem("newticket_filtro_ruolo", JSON.stringify(stored));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
  }, []);

  // Anti-doppione: cerca manutenzioni recenti dello stesso condominio e categoria
  useEffect(() => {
    if (!form.condominio_id || !form.categoria) { setRecenti([]); return; }
    const params = { categoria: form.categoria };
    if (user.role !== "condomino") params.condominio_id = form.condominio_id;
    api.get("/segnalazioni-recenti", { params })
      .then((r) => { setRecenti(r.data || []); setDupAck(false); })
      .catch(() => setRecenti([]));
  }, [form.condominio_id, form.categoria, user.role]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const condNome = condomini.find((c) => c.id === form.condominio_id)?.nome || "—";

  // Carica i condòmini del condominio selezionato (per allegarli come referenti)
  useEffect(() => {
    if (!form.condominio_id) { setContatti([]); return; }
    api.get(`/condomini/${form.condominio_id}/contatti`).then((r) => setContatti(r.data || [])).catch(() => setContatti([]));
    setForm((f) => ({ ...f, contatti_referenti: [] }));
  }, [form.condominio_id]);

  const sameContatto = (a, b) => a.nome === b.nome && (a.unita || "") === (b.unita || "");
  const toggleContatto = (c) => {
    setForm((f) => {
      const exists = f.contatti_referenti.some((x) => sameContatto(x, c));
      const list = exists ? f.contatti_referenti.filter((x) => !sameContatto(x, c)) : [...f.contatti_referenti, c];
      return { ...f, contatti_referenti: list };
    });
  };

  // Gestione (admin): fornitori consigliati per la categoria scelta
  useEffect(() => {
    if (user.role !== "admin" || !form.categoria) { setSuggested([]); return; }
    api.get("/fornitori", { params: { categoria: form.categoria, limit: 500 } })
      .then((r) => setSuggested(r.data.fornitori || [])).catch(() => setSuggested([]));
  }, [form.categoria, user.role]);

  // Gestione (admin): ricerca fornitori (debounced)
  useEffect(() => {
    if (user.role !== "admin" || !fornSearch.trim()) { setSearchResults([]); return; }
    const tm = setTimeout(() => {
      api.get("/fornitori", { params: { search: fornSearch, limit: 50 } })
        .then((r) => setSearchResults(r.data.fornitori || [])).catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(tm);
  }, [fornSearch, user.role]);

  const toggleForn = (fid) => setSelForn((p) => p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]);

  // Quando cambia la priorità e la scadenza è in modalità automatica, ricalcola la data
  const setPriorita = (p) => {
    setForm((f) => ({ ...f, priorita: p, ...(scadenzaAuto ? { data_scadenza: addDays(SLA_GIORNI[p] ?? 7) } : {}) }));
  };
  const setScadenza = (v) => {
    setScadenzaAuto(false);
    set("data_scadenza", v);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const canNext = () => {
    if (step === 0) return form.categoria && form.condominio_id;
    if (step === 1) return form.titolo.trim() && form.descrizione.trim() && form.condomino_nome.trim();
    return true;
  };

  const submit = async () => {
    if (recenti.length > 0 && !dupAck) { setShowDup(true); return; }
    await doSubmit();
  };

  const doSubmit = async () => {
    setShowDup(false);
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, privacy_consenso: privacyOk, foto_ids: photos.map((p) => p.id), allegati_ids: allegati.map((a) => a.id) };
      if (user.role === "condomino") payload.data_scadenza = "";
      const { data } = await api.post("/tickets", payload);
      // Gestione (admin): affido immediato ai fornitori selezionati
      if (user.role === "admin" && selForn.length > 0) {
        try {
          const r = await api.post(`/tickets/${data.id}/assign-multi`, { fornitore_ids: selForn, note: assignNote });
          const n = r.data?.count || selForn.length;
          toast.success(n > 1 ? `Manutenzione creata e affidata a ${n} fornitori (${n - 1} copia/e).` : "Manutenzione creata e affidata al fornitore.");
        } catch (err) {
          toast.warning("Manutenzione creata, ma l'affido al fornitore non è riuscito. Puoi affidarla dal dettaglio.");
        }
      }
      navigate(`/segnalazioni/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setSaving(false);
    }
  };

  const associa = async (t) => {
    try {
      await api.post(`/tickets/${t.id}/associa`, {
        nome: form.condomino_nome || user.name, unita: form.unita, telefono: form.telefono_contatto,
      });
      toast.success("Ti sei associato alla manutenzione esistente. Grazie!");
      navigate(`/segnalazioni/${t.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const RecentWarning = () => recenti.length === 0 ? null : (
    <div data-testid="dup-warning" className="mt-6 p-4 rounded-xl border-2 border-amber-300 bg-amber-50">
      <div className="flex items-center gap-2 text-amber-800 font-bold text-base mb-2">
        <AlertTriangle className="w-5 h-5" /> Forse è già stata segnalata
      </div>
      <p className="text-base text-amber-900/90 mb-3">
        Nel tuo condominio {recenti.length === 1 ? "c'è già" : "ci sono già"} <strong>{recenti.length}</strong> manutenzion{recenti.length === 1 ? "e" : "i"} recenti per «{form.categoria}». Se è la tua, <strong>associati</strong> invece di aprirne una nuova:
      </p>
      <div className="space-y-2">
        {recenti.map((t) => (
          <div key={t.id} data-testid={`dup-item-${t.id}`}
            className="bg-white rounded-lg border border-amber-200 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-800">{t.titolo}</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0">{t.stato}</span>
            </div>
            {t.descrizione && <div className="text-sm text-slate-500 mt-0.5 line-clamp-1">{t.descrizione}</div>}
            <div className="text-xs text-slate-400 mt-1">
              {t.numero} · {new Date(t.created_at).toLocaleDateString("it-IT")}{t.unita ? ` · ${t.unita}` : ""}{t.condomino_nome ? ` · ${t.condomino_nome}` : ""}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button type="button" data-testid={`dup-open-${t.id}`} variant="outline" size="sm" className="h-9"
                onClick={() => window.open(`/segnalazioni/${t.id}`, "_blank", "noopener")}>
                Apri scheda
              </Button>
              {user.role === "condomino" && (
                <Button type="button" data-testid={`dup-associa-${t.id}`} size="sm"
                  className="h-9 bg-amber-700 hover:bg-amber-800"
                  onClick={() => associa(t)}>
                  È la mia: mi associo
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-2 mb-2">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Nuova Manutenzione</h1>
        <InfoHint section="nuova-segnalazione" />
      </div>
      <p className="text-lg text-slate-600 mb-4">Segui i passaggi guidati per inviare la tua richiesta di manutenzione.</p>

      {/* Stepper */}
      <div className="flex items-center mb-5">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className={`flex items-center gap-2 ${i <= step ? "text-brand" : "text-slate-400"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${
                i < step ? "bg-brand text-white border-brand" : i === step ? "border-brand text-brand" : "border-slate-300"
              }`}>
                {i < step ? <CheckCircle2 className="w-6 h-6" /> : i + 1}
              </div>
              <span className="font-semibold hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-1 mx-3 rounded ${i < step ? "bg-brand" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <Card className="p-4 md:p-5 border-2">
        {/* STEP 0 */}
        {step === 0 && (
          <div>
            {user.role === "admin" && (
              <div className="mb-4">
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Condominio<span className="text-red-500 ml-0.5">*</span></Label>
                <select data-testid="wizard-condominio" value={form.condominio_id} onChange={(e) => set("condominio_id", e.target.value)}
                  className="h-14 mt-2 w-full text-lg border-2 border-slate-300 rounded-lg px-3 bg-white">
                  <option value="">Seleziona condominio...</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700 block">Tipo di problema<span className="text-red-500 ml-0.5">*</span></Label>
              {user.role === "admin" && (
                <button type="button" data-testid="wizard-gestisci-categorie" onClick={() => setCatOpen(true)}
                  className="text-brand text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                  <Tags className="w-4 h-4" /> Gestisci categorie
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {meta.categorie.map((cat) => {
                const Icon = ICONS[cat] || HelpCircle;
                const active = form.categoria === cat;
                return (
                  <button key={cat} data-testid={`wizard-cat-${cat}`} onClick={() => set("categoria", cat)}
                    className={`flex flex-col items-center justify-center gap-2 p-5 rounded-xl border-2 min-h-[110px] transition-colors ${
                      active ? "border-brand bg-[#FBEEF3]" : "border-slate-200 hover:border-[#C98AA0]"
                    }`}>
                    <Icon className={`w-9 h-9 ${CAT_COLORS[cat] || "text-slate-500"}`} />
                    <span className="font-semibold text-center text-sm">{cat}</span>
                  </button>
                );
              })}
            </div>
            <RecentWarning />
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Titolo<span className="text-red-500 ml-0.5">*</span></Label>
              <Input data-testid="wizard-titolo" value={form.titolo} onChange={(e) => set("titolo", e.target.value)}
                className="h-14 text-lg mt-2" placeholder="Es. Perdita d'acqua in cantina" />
            </div>
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Descrizione<span className="text-red-500 ml-0.5">*</span></Label>
              <Textarea data-testid="wizard-descrizione" value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)}
                className="text-lg mt-2 min-h-[120px]" placeholder="Descrivi il problema con il maggior dettaglio possibile..." />
            </div>
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Priorità della manutenzione</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                {(meta.priorita || []).map((p) => (
                  <button key={p} data-testid={`wizard-priorita-${p}`} onClick={() => setPriorita(p)}
                    className={`h-14 rounded-lg border-2 font-bold text-base ${
                      form.priorita === p ? PRIORITA_STYLE[p] : "border-slate-200 text-slate-600"
                    }`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Data di scadenza</Label>
                {user.role === "condomino" ? (
                  <>
                    <div data-testid="wizard-scadenza-auto"
                      className="h-14 mt-2 flex items-center px-4 rounded-md border-2 border-slate-200 bg-slate-50 text-lg font-semibold text-slate-700">
                      {fmtDate(form.data_scadenza)}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Calcolata in automatico dal sistema in base alla priorità ({form.priorita}).
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mt-2">
                      <DatePickerField testid="wizard-scadenza" value={form.data_scadenza}
                        onChange={setScadenza} disablePast allowClear={false} className="h-14 text-lg" />
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {scadenzaAuto ? `Calcolata in automatico dalla priorità (${form.priorita}). Puoi modificarla.` : "Data impostata manualmente."}
                    </p>
                  </>
                )}
              </div>
              <div>
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Tipo</Label>
                <div className="flex gap-3 mt-2">
                  {meta.tipi.map((t) => (
                    <button key={t} data-testid={`wizard-tipo-${t}`} onClick={() => set("tipo", t)}
                      className={`flex-1 h-14 rounded-lg border-2 font-bold text-base ${
                        form.tipo === t ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200"
                      }`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Segnalatore/Condòmino<span className="text-red-500 ml-0.5">*</span></Label>
                <Input data-testid="wizard-condomino" value={form.condomino_nome} onChange={(e) => set("condomino_nome", e.target.value)}
                  className="h-14 text-lg mt-2" placeholder="Nome e cognome del segnalatore/condòmino" />
              </div>
              <div>
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Interno / Unità (opz.)</Label>
                <Input data-testid="wizard-unita" value={form.unita} onChange={(e) => set("unita", e.target.value)}
                  className="h-14 text-lg mt-2" placeholder="Es. Scala A, int. 5" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Telefono di contatto</Label>
                <Input data-testid="wizard-telefono" value={form.telefono_contatto} onChange={(e) => set("telefono_contatto", e.target.value)}
                  className="h-14 text-lg mt-2" placeholder="333 1234567" />
              </div>
            </div>
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Contatti del condòmino per il fornitore</Label>
              <p className="text-base text-slate-500 mb-2">Seleziona i condòmini da contattare: <strong>nome e telefono</strong> saranno visibili al fornitore incaricato.</p>
              <button type="button" data-testid="wizard-contatti-toggle" onClick={() => setContattiOpen((o) => !o)}
                className="w-full h-14 flex items-center justify-between px-4 rounded-lg border-2 border-slate-300 bg-white text-lg text-left">
                <span className={form.contatti_referenti.length ? "text-slate-800 font-semibold" : "text-slate-400"}>
                  {form.contatti_referenti.length ? `${form.contatti_referenti.length} condòmino/i selezionati` : "Seleziona i condòmini..."}
                </span>
                <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${contattiOpen ? "rotate-180" : ""}`} />
              </button>
              {contattiOpen && (
                <div data-testid="wizard-contatti-list" className="mt-2 border-2 border-slate-200 rounded-lg max-h-64 overflow-auto">
                  {/* Filtro ruolo (Iter 3) */}
                  <div className="sticky top-0 bg-slate-50 border-b p-2 flex flex-wrap gap-1.5" data-testid="wizard-contatti-filter">
                    {[
                      { key: "tutti", label: "Tutti", roles: null },
                      { key: "proprietari", label: "Solo Proprietari", roles: ["Proprietario", "Comproprietario"] },
                      { key: "inquilini", label: "Solo Inquilini", roles: ["Inquilino/Conduttore"] },
                    ].map((f) => (
                      <button key={f.key} type="button" data-testid={`filter-${f.key}`}
                        onClick={() => setAndRememberFiltro(f.roles)}
                        className={`h-8 px-3 rounded-full text-sm font-semibold border-2 transition-colors ${
                          (filtroRuolo === f.roles || (filtroRuolo === null && f.roles === null))
                            ? "bg-brand text-white border-brand"
                            : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                        }`}>{f.label}</button>
                    ))}
                  </div>
                  <div className="divide-y">
                  {contattiFiltrati.length === 0 ? (
                    <div className="p-3 text-base text-slate-500">Nessun condòmino trovato con questo filtro.</div>
                  ) : (
                    contattiFiltrati.map((c, i) => {
                      const checked = form.contatti_referenti.some((x) => sameContatto(x, c));
                      return (
                        <label key={i} data-testid={`wizard-contatto-${i}`}
                          className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={checked} onChange={() => toggleContatto(c)} className="w-5 h-5 accent-brand shrink-0" />
                          <div className="min-w-0">
                            <div className="font-semibold flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              {c.nome}
                              {c.unita && <span className="text-sm text-slate-500">int. {c.unita}</span>}
                              {c.ruolo && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBEEF3] text-brand">{c.ruolo}</span>}
                            </div>
                            <div className="text-sm text-slate-500">{c.telefono || "Nessun telefono in anagrafica"}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                  </div>
                </div>
              )}
              {form.contatti_referenti.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.contatti_referenti.map((c, i) => (
                    <span key={i} data-testid={`wizard-contatto-chip-${i}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBEEF3] text-brand text-sm font-semibold">
                      <Users className="w-3.5 h-3.5" /> {c.nome}{c.telefono ? ` · ${c.telefono}` : ""}
                      <button type="button" onClick={() => toggleContatto(c)} aria-label="Rimuovi"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">Foto del guasto (opzionale)</Label>
              <p className="text-base text-slate-500 mb-2">Scatta una foto o scegline una dalla galleria del telefono per aiutare il fornitore.</p>
              <PhotoUploader photos={photos} onChange={setPhotos} />
            </div>
            <div>
              <Label className="text-base font-bold uppercase tracking-wide text-slate-700">File e documenti (opzionale)</Label>
              <p className="text-base text-slate-500 mb-2">Allega documenti dall'archivio del telefono o dal computer (es. PDF, preventivi, schede tecniche).</p>
              <DocumentUploader docs={allegati} onChange={setAllegati} />
            </div>
            {user.role === "admin" && (
              <div data-testid="wizard-gestione" className="rounded-xl border-2 border-[#EBC5D2] bg-[#FBEEF3]/40 p-4 space-y-3">
                <div className="flex items-center gap-2 text-brand">
                  <UserCheck className="w-6 h-6" />
                  <h3 className="font-display text-xl font-bold">Gestione — Affida subito (opzionale)</h3>
                </div>
                <p className="text-sm text-slate-600 -mt-1">Solo per lo Studio. Puoi <b>affidare subito</b> la manutenzione a uno o più fornitori: sarà affidata al 1° selezionato e, per ciascun altro, verrà creata una copia già affidata (ognuno riceve il proprio incarico). Lascia vuoto per affidarla in un secondo momento.</p>
                <Input data-testid="wizard-assign-search" value={fornSearch} onChange={(e) => setFornSearch(e.target.value)}
                  className="h-12 bg-white" placeholder="Cerca fornitore per nome, settore o città..." />
                <div data-testid="wizard-assign-list" className="w-full border-2 border-slate-300 rounded-lg bg-white max-h-60 overflow-y-auto overflow-x-hidden divide-y">
                  {(() => {
                    const showSearch = fornSearch.trim().length > 0;
                    const lista = showSearch ? searchResults : suggested;
                    if (lista.length === 0) {
                      return <div className="p-3 text-base text-slate-500">{showSearch ? "Nessun fornitore trovato." : (form.categoria ? `Nessun fornitore consigliato per ${form.categoria}: usa la ricerca.` : "Scegli una categoria o cerca un fornitore.")}</div>;
                    }
                    return (
                      <>
                        <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 sticky top-0">
                          {showSearch ? "Risultati ricerca" : `Consigliati per ${form.categoria}`}
                        </div>
                        {lista.map((f) => {
                          const checked = selForn.includes(f.id);
                          return (
                            <button type="button" key={f.id} data-testid={`wizard-assign-opt-${f.id}`} onClick={() => toggleForn(f.id)}
                              className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3 ${checked ? "bg-[#FBEEF3]" : ""}`}>
                              {checked ? <CheckSquare className="w-5 h-5 text-brand shrink-0" /> : <Square className="w-5 h-5 text-slate-400 shrink-0" />}
                              <span className="min-w-0">
                                <span className="block font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, fornSearch)}{f.rating_medio ? ` ⭐ ${f.rating_medio}` : ""}</span>
                                <span className="block text-sm text-slate-500 truncate">{highlightMatch([f.settore || (f.categorie || [])[0], f.citta].filter(Boolean).join(" · ") || "—", fornSearch)}</span>
                              </span>
                            </button>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
                {selForn.length > 0 && (
                  <div data-testid="wizard-assign-selected" className="text-sm font-bold text-brand">
                    {selForn.length} {selForn.length === 1 ? "fornitore selezionato" : "fornitori selezionati"}: {suggested.concat(searchResults).filter((f) => selForn.includes(f.id)).map((f) => f.ragione_sociale).join(", ")}
                  </div>
                )}
                <Input data-testid="wizard-assign-note" value={assignNote} onChange={(e) => setAssignNote(e.target.value)}
                  className="h-12 bg-white" placeholder="Nota per il/i fornitore/i (opzionale)" />
              </div>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-display text-2xl font-bold">Controlla e conferma</h3>
            {[
              ["Condominio", condNome], ["Categoria", form.categoria], ["Priorità", form.priorita],
              ["Scadenza", fmtDate(form.data_scadenza)],
              ["Tipo", form.tipo], ["Titolo", form.titolo], ["Descrizione", form.descrizione],
              ["Segnalatore / Condòmino", form.condomino_nome], ["Unità", form.unita || "—"], ["Telefono", form.telefono_contatto || "—"],
              ["Contatti per il fornitore", form.contatti_referenti.length ? form.contatti_referenti.map((c) => `${c.nome}${c.telefono ? ` (${c.telefono})` : ""}`).join(", ") : "—"],
              ...(user.role === "admin" ? [["Affido immediato", selForn.length ? suggested.concat(searchResults).filter((f) => selForn.includes(f.id)).map((f) => f.ragione_sociale).join(", ") : "Nessuno (affido successivo)"]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-slate-100">
                <div className="w-40 shrink-0 text-sm font-bold uppercase tracking-wide text-slate-500">{k}</div>
                <div className="text-lg text-slate-800">{v}</div>
              </div>
            ))}
            {photos.length > 0 && (
              <div className="pt-2">
                <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">Foto allegate ({photos.length})</div>
                <PhotoUploader photos={photos} onChange={setPhotos} testid="photo-uploader-review" />
              </div>
            )}
            {allegati.length > 0 && (
              <div className="pt-2">
                <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">File e documenti ({allegati.length})</div>
                <DocumentUploader docs={allegati} onChange={setAllegati} testid="document-uploader-review" />
              </div>
            )}
            <PrivacyConsent checked={privacyOk} onChange={setPrivacyOk} />
            <RecentWarning />
          </div>
        )}

        {error && (
          <div data-testid="wizard-error" className="mt-5 p-4 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg flex items-center gap-2 font-semibold">
            <AlertTriangle className="w-5 h-5" /> {error}
          </div>
        )}

        {/* Nav */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-8">
          <Button data-testid="wizard-back" variant="outline" className="h-14 px-6 text-lg"
            onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))}>
            <ArrowLeft className="w-5 h-5 mr-2" /> Indietro
          </Button>
          {step < 2 ? (
            <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep(step + 1)}
              className="h-14 px-6 text-lg font-bold bg-brand hover:bg-brand-dark">
              Avanti <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button data-testid="wizard-submit" disabled={saving || !privacyOk} onClick={submit}
              className="h-14 px-6 text-lg font-bold bg-green-700 hover:bg-green-800 max-w-full whitespace-normal">
              <CheckCircle2 className="w-5 h-5 mr-2 shrink-0" /> {saving ? "Invio..." : "Invia Manutenzione"}
            </Button>
          )}
        </div>
      </Card>

      {showDup && (
        <div data-testid="dup-confirm-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full p-4 border-2 max-h-[90vh] overflow-auto">
            <div className="flex items-center gap-2 text-amber-700 font-bold text-xl mb-2">
              <AlertTriangle className="w-6 h-6" /> Verifica prima di inviare
            </div>
            <p className="text-base text-slate-700 mb-4">
              {recenti.length === 1 ? "C'è già" : "Ci sono già"} <strong>{recenti.length}</strong> manutenzion{recenti.length === 1 ? "e" : "i"} recenti per «{form.categoria}» nel tuo condominio. Se è la stessa, <strong>associati</strong> invece di inviarne un'altra.
            </p>
            <div className="space-y-2 mb-5">
              {recenti.map((t) => (
                <div key={t.id} className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-800">{t.titolo}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0">{t.stato}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{t.numero} · {new Date(t.created_at).toLocaleDateString("it-IT")}</div>
                  {user.role === "condomino" && (
                    <Button type="button" data-testid={`dup-modal-associa-${t.id}`} size="sm"
                      className="h-9 mt-2 bg-amber-700 hover:bg-amber-800" onClick={() => associa(t)}>
                      È la mia: mi associo
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="dup-cancel" variant="outline" className="h-12 flex-1 text-base"
                onClick={() => setShowDup(false)}>
                Controllo meglio
              </Button>
              <Button data-testid="dup-proceed" className="h-12 flex-1 text-base font-bold bg-green-700 hover:bg-green-800"
                onClick={() => { setDupAck(true); doSubmit(); }}>
                È diversa, invia comunque
              </Button>
            </div>
          </Card>
        </div>
      )}
      <CategorieManager open={catOpen} onOpenChange={setCatOpen} onChange={loadMeta} />
    </div>
  );
}
